import React from "react";
import { URLS } from "Utils/apiURL";
import { FIND_EMAIL_FIELDS } from "Pages/Dashboard/data-toolkit/constants";
import StatusPill from "Pages/Dashboard/data-toolkit/StatusPill";
import ToolRunner from "Pages/Dashboard/data-toolkit/ToolRunner";

const cellHas = (row, idx) =>
  idx != null && ((row && row[idx]) || "").trim() !== "";

const hasFindableColumns = mapping => {
  if (mapping.linkedinUrl != null) return true;
  const hasName =
    mapping.fullName != null ||
    (mapping.firstName != null && mapping.lastName != null);
  const hasCompany =
    mapping.companyDomain != null || mapping.companyName != null;
  return hasName && hasCompany;
};

// Per-row worst-case credits, mirroring the backend: name + company = 1,
// LinkedIn = 2, neither = 0 (skipped).
const rowMaxCredits = (row, m) => {
  const hasName =
    cellHas(row, m.fullName) ||
    (cellHas(row, m.firstName) && cellHas(row, m.lastName));
  const hasCompany = cellHas(row, m.companyDomain) || cellHas(row, m.companyName);
  if (hasName && hasCompany) return 1;
  if (cellHas(row, m.linkedinUrl)) return 2;
  return 0;
};

const FIND_STATS = (counts, job) => [
  { label: "Valid emails", value: counts.found || 0 },
  { label: "Risky", value: counts.risky || 0 },
  { label: "Not found", value: counts.notFound || 0 },
  ...(counts.skipped ? [{ label: "Skipped", value: counts.skipped }] : []),
  { label: "Credits used", value: (job && job.creditsCharged) || 0, brand: true }
];

const FIND_COLUMNS = [
  {
    header: "Email",
    cell: r => (r.email ? <a href={`mailto:${r.email}`}>{r.email}</a> : "-")
  },
  { header: "Status", cell: r => <StatusPill status={r.emailStatus} /> },
  { header: "Title", cell: r => r.jobTitle || "-" },
  { header: "Company", cell: r => r.company || "-" }
];

const CONFIG = {
  toolKey: "FIND_EMAIL",
  iconKey: "find-email",
  title: "Find email",
  description:
    "Find verified work emails from a name and company, or a LinkedIn URL. 1 credit per email found from a name and company, 2 from a LinkedIn URL, and nothing when no email is found.",
  fields: FIND_EMAIL_FIELDS,
  endpoint: URLS.dataToolkitFindEmail,
  runNoun: "Find email run",
  runLabel: "Find emails",
  foundLabel: "Found",
  inputSub:
    "Paste rows or upload a CSV. Each record needs a name and company (domain or name), or a LinkedIn URL.",
  pastePlaceholder:
    "first name, last name, company domain\nElon, Musk, tesla.com",
  mappingHint: (
    <>
      To find an email we need <b>either</b> a LinkedIn URL, <b>or</b> a name
      (full name, or first and last) together with a company (domain or name).
    </>
  ),
  requiredCheck: hasFindableColumns,
  requiredError:
    "Map either a LinkedIn URL column, or a name (full name, or first and last) plus a company (domain or name).",
  rowCredits: rowMaxCredits,
  costNote: max => (
    <>
      This run will use up to <b>{max.toLocaleString()}</b> email credit
      {max === 1 ? "" : "s"}. You're only charged for emails we actually find (1
      per name and company match, 2 per LinkedIn match).
    </>
  ),
  stats: FIND_STATS,
  columns: FIND_COLUMNS
};

const FindEmail = () => <ToolRunner cfg={CONFIG} />;

export default FindEmail;
